import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

interface DocumentRequestTemplateProps {
  preview?: boolean;
  recipientName?: string;
  documentType?: string;
  dueDate?: string;
  taxAgentName?: string;
}

export class DocumentRequestTemplate {
  htmlContent: string;
  textContent: string;

  constructor({
    recipientName = "John Smith",
    documentType = "Income Statement",
    dueDate = "30 June 2024",
    taxAgentName = "Sarah Johnson",
  } = {}) {
    this.htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Document Request - Numberz Insight</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
        }
        .container {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }
        .header {
          background-color: #f8f9fa;
          padding: 20px;
          text-align: center;
          border-bottom: 1px solid #e9ecef;
        }
        .content {
          padding: 20px;
        }
        .footer {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e9ecef;
          font-size: 12px;
          color: #6c757d;
        }
        .button {
          display: inline-block;
          background-color: #0066cc;
          color: white;
          padding: 10px 20px;
          text-decoration: none;
          border-radius: 4px;
          margin-top: 15px;
        }
        .info-box {
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 4px;
          padding: 15px;
          margin: 20px 0;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>Document Request</h1>
        </div>

        <div class="content">
          <p>Dear ${recipientName},</p>

          <p>I hope this email finds you well. As part of preparing your tax return, we need your <strong>${documentType}</strong> to ensure we can accurately complete your filing.</p>

          <div class="info-box">
            <h3>Document Requested: ${documentType}</h3>
            <p>Please provide this document by <strong>${dueDate}</strong> to avoid any delays in processing your tax return.</p>
          </div>

          <p>You can upload this document securely through your Numberz Insight portal or reply to this email with the document attached.</p>

          <a href="https://www.numberz.au/dashboard/documents/upload" class="button">Upload Document</a>

          <p style="margin-top: 20px;">If you have any questions or need assistance locating this document, please don't hesitate to contact me directly.</p>

          <p>Thank you for your prompt attention to this matter.</p>

          <p>Best regards,<br>${taxAgentName}<br>Tax Agent<br>Numberz Insight Pty Ltd</p>
        </div>

        <div class="footer">
          <p><strong>NUMBERZ INSIGHT PTY LTD</strong><br>
          ABN: 55 644 962 167<br>
          Unit 41/2 Fastline Rd, Truganina VIC 3029<br>
          Email: tax.agents@numberz.au<br>
          Phone: 1300 123 435</p>

          <p>This email is intended only for the person or entity to which it is addressed and may contain confidential and/or privileged material. Any review, retransmission, dissemination or other use of this information by persons or entities other than the intended recipient is prohibited.</p>

          <p>
            <a href="https://www.numberz.au/email/preferences?email={{email}}&token={{token}}">Email Preferences</a> |
            <a href="https://www.numberz.au/email/unsubscribe?email={{email}}&token={{token}}&type=document_requests">Unsubscribe</a> |
            <a href="https://www.numberz.au/email/privacy-policy">Privacy Policy</a>
          </p>
        </div>
      </div>
    </body>
    </html>
  `;

    this.textContent = `
Document Request - Numberz Insight

Dear ${recipientName},

I hope this email finds you well. As part of preparing your tax return, we need your ${documentType} to ensure we can accurately complete your filing.

Document Requested: ${documentType}
Please provide this document by ${dueDate} to avoid any delays in processing your tax return.

You can upload this document securely through your Numberz Insight portal or reply to this email with the document attached.

To upload your document, please visit: https://www.numberz.au/dashboard/documents/upload

If you have any questions or need assistance locating this document, please don't hesitate to contact me directly.

Thank you for your prompt attention to this matter.

Best regards,
${taxAgentName}
Tax Agent
Numberz Insight Pty Ltd

---

NUMBERZ INSIGHT PTY LTD
ABN: 55 644 962 167
Unit 41/2 Fastline Rd, Truganina VIC 3029
Email: tax.agents@numberz.au
Phone: 1300 123 435

This email is intended only for the person or entity to which it is addressed and may contain confidential and/or privileged material. Any review, retransmission, dissemination or other use of this information by persons or entities other than the intended recipient is prohibited.

Email Preferences: https://www.numberz.au/email/preferences?email={{email}}&token={{token}}
Unsubscribe: https://www.numberz.au/email/unsubscribe?email={{email}}&token={{token}}&type=document_requests
Privacy Policy: https://www.numberz.au/email/privacy-policy
  `;
  }
}

export function DocumentRequestTemplateComponent({
  preview = false,
  recipientName = "John Smith",
  documentType = "Income Statement",
  dueDate = "30 June 2024",
  taxAgentName = "Sarah Johnson",
}: DocumentRequestTemplateProps) {
  const [view, setView] = useState<"html" | "text" | "preview">(
    preview ? "preview" : "html"
  );

  const templateInstance = new DocumentRequestTemplate({
    recipientName,
    documentType,
    dueDate,
    taxAgentName,
  });

  const htmlContent = templateInstance.htmlContent;
  const textContent = templateInstance.textContent;

  /* Original template string for reference
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Document Request - Numberz Insight</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
        }
        .container {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }
        .header {
          background-color: #f8f9fa;
          padding: 20px;
          text-align: center;
          border-bottom: 1px solid #e9ecef;
        }
        .content {
          padding: 20px;
        }
        .footer {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e9ecef;
          font-size: 12px;
          color: #6c757d;
        }
        .button {
          display: inline-block;
          background-color: #0066cc;
          color: white;
          padding: 10px 20px;
          text-decoration: none;
          border-radius: 4px;
          margin-top: 15px;
        }
        .info-box {
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 4px;
          padding: 15px;
          margin: 20px 0;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>Document Request</h1>
        </div>

        <div class="content">
          <p>Dear ${recipientName},</p>

          <p>I hope this email finds you well. As part of preparing your tax return, we need your <strong>${documentType}</strong> to ensure we can accurately complete your filing.</p>

          <div class="info-box">
            <h3>Document Requested: ${documentType}</h3>
            <p>Please provide this document by <strong>${dueDate}</strong> to avoid any delays in processing your tax return.</p>
          </div>

          <p>You can upload this document securely through your Numberz Insight portal or reply to this email with the document attached.</p>

          <a href="https://www.numberz.au/dashboard/documents/upload" class="button">Upload Document</a>

          <p style="margin-top: 20px;">If you have any questions or need assistance locating this document, please don't hesitate to contact me directly.</p>

          <p>Thank you for your prompt attention to this matter.</p>

          <p>Best regards,<br>${taxAgentName}<br>Tax Agent<br>Numberz Insight Pty Ltd</p>
        </div>

        <div class="footer">
          <p><strong>NUMBERZ INSIGHT PTY LTD</strong><br>
          ABN: 55 644 962 167<br>
          Unit 41/2 Fastline Rd, Truganina VIC 3029<br>
          Email: tax.agents@numberz.au<br>
          Phone: 1300 123 435</p>

          <p>This email is intended only for the person or entity to which it is addressed and may contain confidential and/or privileged material. Any review, retransmission, dissemination or other use of this information by persons or entities other than the intended recipient is prohibited.</p>

          <p>
            <a href="https://www.numberz.au/email/preferences?email={{email}}&token={{token}}">Email Preferences</a> |
            <a href="https://www.numberz.au/email/unsubscribe?email={{email}}&token={{token}}&type=document_requests">Unsubscribe</a> |
            <a href="https://www.numberz.au/email/privacy-policy">Privacy Policy</a>
          </p>
        </div>
      </div>
    </body>
    </html>
  `;*/

  /* Original text content for reference
  const textContent = `
Document Request - Numberz Insight

Dear ${recipientName},

I hope this email finds you well. As part of preparing your tax return, we need your ${documentType} to ensure we can accurately complete your filing.

Document Requested: ${documentType}
Please provide this document by ${dueDate} to avoid any delays in processing your tax return.

You can upload this document securely through your Numberz Insight portal or reply to this email with the document attached.

To upload your document, please visit: https://www.numberz.au/dashboard/documents/upload

If you have any questions or need assistance locating this document, please don't hesitate to contact me directly.

Thank you for your prompt attention to this matter.

Best regards,
${taxAgentName}
Tax Agent
Numberz Insight Pty Ltd

---

NUMBERZ INSIGHT PTY LTD
ABN: 55 644 962 167
Unit 41/2 Fastline Rd, Truganina VIC 3029
Email: tax.agents@numberz.au
Phone: 1300 123 435

This email is intended only for the person or entity to which it is addressed and may contain confidential and/or privileged material. Any review, retransmission, dissemination or other use of this information by persons or entities other than the intended recipient is prohibited.

Email Preferences: https://www.numberz.au/email/preferences?email={{email}}&token={{token}}
Unsubscribe: https://www.numberz.au/email/unsubscribe?email={{email}}&token={{token}}&type=document_requests
Privacy Policy: https://www.numberz.au/email/privacy-policy
  `;*/

  return (
    <Card className="w-full bg-gradient-to-b from-gray-900 to-gray-950 border-gray-800 shadow-xl">
      <CardHeader className="bg-gray-900/50 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-gray-100">
              Document Request Template
            </CardTitle>
            <CardDescription className="text-gray-400">
              Template for requesting tax documents from clients
            </CardDescription>
          </div>
          <Badge
            variant="outline"
            className="bg-blue-900/30 text-blue-300 border-blue-800/50"
          >
            Transactional
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={view} onValueChange={(v) => setView(v as any)}>
          <TabsList className="grid w-full grid-cols-3 bg-gray-900 border border-gray-800 p-1 rounded-lg">
            <TabsTrigger
              value="preview"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg"
            >
              Preview
            </TabsTrigger>
            <TabsTrigger
              value="html"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg"
            >
              HTML
            </TabsTrigger>
            <TabsTrigger
              value="text"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg"
            >
              Plain Text
            </TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="mt-4">
            <div className="overflow-auto max-h-[500px] bg-gradient-to-b from-blue-950 to-gray-950 rounded-lg shadow-lg border border-blue-900/50">
              <div className="p-8">
                <div className="flex justify-center mb-6">
                  <div className="bg-blue-500/10 backdrop-blur-sm p-2 rounded-lg border border-blue-500/20 inline-block">
                    <h2 className="text-2xl font-semibold text-blue-300">
                      Document Request
                    </h2>
                  </div>
                </div>

                <div className="space-y-5 text-gray-200">
                  <p className="text-lg">Dear {recipientName},</p>

                  <p className="leading-relaxed">
                    I hope this email finds you well. As part of preparing your
                    tax return, we need your{" "}
                    <span className="font-medium text-blue-300">
                      {documentType}
                    </span>{" "}
                    to ensure we can accurately complete your filing.
                  </p>

                  <div className="bg-blue-900/30 backdrop-blur-sm border border-blue-800/50 rounded-lg p-5 my-6 shadow-inner">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1.5 h-6 bg-blue-400 rounded-full"></div>
                      <h3 className="font-medium text-blue-300 text-lg">
                        Document Requested: {documentType}
                      </h3>
                    </div>
                    <p className="text-gray-300">
                      Please provide this document by{" "}
                      <span className="font-medium text-yellow-300">
                        {dueDate}
                      </span>{" "}
                      to avoid any delays in processing your tax return.
                    </p>
                  </div>

                  <p className="leading-relaxed">
                    You can upload this document securely through your Numberz
                    Insight portal or reply to this email with the document
                    attached.
                  </p>

                  <div className="my-6 flex justify-center">
                    <Button className="bg-blue-600 hover:bg-blue-500 text-white border border-blue-500/50 shadow-lg transition-all hover:shadow-blue-500/20 hover:scale-105">
                      Upload Document
                    </Button>
                  </div>

                  <p className="leading-relaxed">
                    If you have any questions or need assistance locating this
                    document, please don't hesitate to contact me directly.
                  </p>

                  <div className="pt-5 mt-6 border-t border-blue-900/30">
                    <p>Best regards,</p>
                    <p className="font-medium text-blue-300">{taxAgentName}</p>
                    <p className="text-gray-400">Tax Agent</p>
                    <p className="text-gray-400">Numberz Insight</p>
                  </div>
                </div>
              </div>

              <div className="p-5 text-xs text-gray-400 bg-gray-950/80 border-t border-blue-900/30">
                <div className="flex justify-between flex-wrap gap-4">
                  <div>
                    <p className="font-medium text-gray-300">
                      NUMBERZ INSIGHT PTY LTD
                    </p>
                    <p>ABN: 55 644 962 167</p>
                    <p>1300 123 435</p>
                  </div>
                  <div className="text-right">
                    <p>Last Updated: 4/20/2025</p>
                    <div className="flex gap-3 mt-2 justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-gray-400 hover:text-white hover:bg-gray-800"
                      >
                        Edit Template
                      </Button>
                      <Button
                        size="sm"
                        className="h-6 px-2 text-xs bg-blue-600 hover:bg-blue-500 text-white"
                        onClick={() => {
                          // This would be connected to the SendTestEmailModal in a real implementation
                          alert(
                            "This button would open the Send Test Email modal"
                          );
                        }}
                      >
                        Send Test
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="html" className="mt-4">
            <div className="bg-gradient-to-b from-gray-900 to-gray-950 rounded-lg border border-gray-800 p-4 max-h-[500px] overflow-auto shadow-lg">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-800">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-xs font-medium text-gray-300">
                    HTML Source
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  Copy Code
                </Button>
              </div>
              <pre className="text-xs text-blue-300 whitespace-pre-wrap font-mono">
                {htmlContent}
              </pre>
            </div>
          </TabsContent>

          <TabsContent value="text" className="mt-4">
            <div className="bg-gradient-to-b from-gray-900 to-gray-950 rounded-lg border border-gray-800 p-4 max-h-[500px] overflow-auto shadow-lg">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-800">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className="text-xs font-medium text-gray-300">
                    Plain Text Version
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-gray-400 hover:text-white hover:bg-gray-800"
                >
                  Copy Code
                </Button>
              </div>
              <pre className="text-xs text-green-300 whitespace-pre-wrap font-mono">
                {textContent}
              </pre>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="flex justify-between bg-gray-900/50 border-t border-gray-800">
        <div className="text-sm text-gray-400">
          Last Updated: {new Date().toLocaleDateString()}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-300 hover:text-white hover:bg-gray-800"
          >
            Edit Template
          </Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-500 text-white"
            onClick={() => {
              // This would be connected to the SendTestEmailModal in a real implementation
              alert("This button would open the Send Test Email modal");
            }}
          >
            Send Test
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
